import { Box, Divider, Grid2, Typography } from "@mui/material";
import PingStatusTooltip from "../../../../Components/PingStatus/PingStatusTooltip";
import * as React from "react";
import ChartCalendar from "../../ChartCalendar";
import { convertAndGetTimeStampInRequiredFormat, getDatePickerDate, getLocalDateFromUTC } from "../../../../Helper/DatePicker/DateConverter";
import CustomIcon from "../../../../Components/CustomIcon";
import { PrimaryTooltip, StatusDot } from "../../../../Components/StyledComponent";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import VitalsChart from "./VitalsChart";
import ApexCharts from 'apexcharts';

import VitalsChartDateCarousel from "./VitalsChartDateCarousel";
import { primaryGranularities, secondaryGranularities } from '../../../../Helper/DigitalTwin/DataFile';
import VitalsChartArea from "./VitalsChartArea";
import { toTitleCase } from "../../../../Helper/Formatters/TextFormatter";
import ChartActions from "./ChartActions";
import { useTranslation } from "react-i18next";
import DownloadCSV from "../../../../Helper/CSVOperations/DownloadCSV";
import { useDispatch } from "react-redux";
import { setCurrentDatesForChart } from "../../../../Actions/DigitalTwinActions";
import { prepareContinuousSignalsData, calculateCarouselData, getBatterySignalsData, getCSVDataForExportFromChart, getDigitalTwinChartData, shouldDisableGranularity, allocateColors } from "../../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { calculateIntervalFromTimeWindow, formatChartTimestampForApi } from "../../../../Helper/Charts/ChartHelper";
import { IDDisplayBox, PrimaryGranularityButton, SecondaryGranularityButton } from "../../../../Theme/DigitalTwinStyles";
import VitalsSelectionDropdown from "../../components/VitalsSelectionDropdown";
import { decryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import BinarySignals from "./BinarySignals/BinarySignals";
import { getPackComparisionV4 } from "../../../../Api/DigitalTwin";
import { availableBinarySignalColors, colorForNoDataBinarySignal } from "../../../DigitalTwin/TabsSection/BatteryVitals/_Constants";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { useGlobalConfig, usePagesContent } from "../../../../Hooks/usePagesContent";
import { getVitalsIdDisplayConfig, resolvePlateLabel, resolveVitalsNodeIdDisplay } from "../../../../Helper/DigitalTwin/vehicleIdentifier";
import Cookies from "universal-cookie";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
const defaultStartDate = getDatePickerDate(undefined, 0)
const defaultEndDate = getDatePickerDate(undefined, 0)

// vitalsSection: the digitalTwin page meta section holding this tab's idDisplay config.
export default function VitalsChartSection({ selectedNode, batteryID, batteryInfo, dataGapDates, vehicleInfo = {}, vitalsSection = "batteryVitals" }) {

    const dispatch = useDispatch();
    const queryParams = decryptTheParams();
    const startDate = queryParams?.startDate || defaultStartDate;

    const { t } = useTranslation(['common', 'digital_twin']);

    const { batteryInfo: batteryInfoData, showBusDeviceInfo } = useBatteryInfo();
    // Plate label: a vehicle org's baseEntity.idLabel, else "Bus Number"; a battery org's
    // idLabel names the device ID, so its plate is "Vehicle Number".
    const vehicleIdLabel = resolvePlateLabel(
        useGlobalConfig(),
        t('digital_twin:section_ids.bus_number', 'Bus Number'),
        t('digital_twin:section_ids.vehicle_number', 'Vehicle Number'),
    );
    let addedOn = batteryInfoData?.addedOn;
    const pageContent = usePagesContent();

    const cookies = new Cookies();
    const timeZone = cookies.get("timeZone");
    if (timeZone) {
        addedOn = moment?.utc(addedOn, "YYYY-MM-DD HH:mm:ss")?.tz(timeZone)?.format("YYYY-MM-DD");
    } else {
        addedOn = moment?.utc(addedOn, "YYYY-MM-DD HH:mm:ss")?.format("YYYY-MM-DD");
    }

    const [currentDates, setCurrentDates] = React.useState({
        startDate: startDate,
        endDate: defaultEndDate
    })

    const [primaryGranularity, setPrimaryGranularity] = React.useState('raw')
    const [secondaryGranularity, setSecondaryGranularity] = React.useState('avg')
    const [selectedContinuousSignals, setSelectedContinuousSignals] = React.useState([]);
    const [allContinuousSignals, setAllContinuousSignals] = React.useState([]);
    const [allBinarySignals, setAllBinarySignals] = React.useState([]);
    const [selectedBinarySignals, setSelectedBinarySignals] = React.useState([]);
    const [binarySignalsData, setBinarySignalsData] = React.useState([]);
    const [showBinarySignals, setShowBinarySignals] = React.useState(false);
    const [series, setSeries] = React.useState([])
    const [selectedDate, setSelectedDate] = React.useState({ key: 0, value: currentDates.startDate })
    const [isContinuousChartLoading, setIsContinuousChartLoading] = React.useState(true)
    const [isBinaryChartLoading, setIsBinaryChartLoading] = React.useState(true)
    const [categories, setCategories] = React.useState([])
    const [visibleSeries, setVisibleSeries] = React.useState(series.map(() => true));
    const [showPrimaryGranularities, setShowPrimaryGranularities] = React.useState(false);
    const [showSecondaryGranularities, setShowSecondaryGranularities] = React.useState(false);
    const [intervalSeconds, setIntervalSeconds] = React.useState(300);
    const [isZoomed, setIsZoomed] = React.useState(false);
    const [zoomedRange, setZoomedRange] = React.useState(null);
    const binarySignalsContainerRef = React.useRef(null);
    const binarySignalsBlockRef = React.useRef(null);
    const zoomTimeoutRef = React.useRef(null);

    const colorMemoryRef = React.useRef(new Map());
    const fetchRequestRef = React.useRef(0);
    const skipSecondaryGranularityEffectRef = React.useRef(false);

    const chartID = 'vitals-chart' + secondaryGranularity;

    const isRawGranularity = primaryGranularity === 'raw';

    const handleDateChange = ({ startDate, endDate }) => {
        setIsContinuousChartLoading(true);
        setIsBinaryChartLoading(true);
        dispatch(setCurrentDatesForChart({ startDate, endDate }))
        setCurrentDates({ startDate, endDate })
    }

    const onPrimaryGranularityChange = (granularity) => {
        if (granularity === primaryGranularity) {
            return false
        }

        setSeries([]);
        setIsContinuousChartLoading(true);
        setPrimaryGranularity(granularity)

        if (granularity == 'raw') {
            // Setting secondaryGranularity here batches with primaryGranularity
            // and fires both [primaryGranularity] and [secondaryGranularity] effects.
            // The [secondaryGranularity] effect reads a stale selectedDate (wrong
            // format for the new granularity) and overwrites the correct date set
            // by [primaryGranularity]. Skip it so only [primaryGranularity] runs.
            skipSecondaryGranularityEffectRef.current = true;
            setSecondaryGranularity('avg')
        }

    }

    const onSecondaryGranularityChange = (granularity) => {
        if (granularity === secondaryGranularity) {
            return false
        }
        setSeries([]);
        setIsContinuousChartLoading(true)
        setSecondaryGranularity(granularity)
    }

    const applySelectedContinuousSignals = (items) => {
        const { coloredSignals, nextAssignment } = allocateColors({
            signals: items,
            prevAssignment: colorMemoryRef.current,
            getKey: s => s.key,
        });
        colorMemoryRef.current = nextAssignment;
        setSelectedContinuousSignals(coloredSignals);
    }

    const fetchChartData = async ({ startDate, endDate, intervalSeconds }) => {
        if (!selectedNode || !selectedNode.id || !selectedContinuousSignals || selectedContinuousSignals.length < 1) {
            setIsContinuousChartLoading(false)
            return false
        }

        const requestId = ++fetchRequestRef.current;
        setIsContinuousChartLoading(true)

        try {
            const [seriesData, chartData, categories] = await getDigitalTwinChartData({
                batteryID: batteryID,
                startDate: startDate,
                endDate: endDate,
                selectedDate: selectedDate.value,
                primaryGranularity: primaryGranularity,
                secondaryGranularity: secondaryGranularity,
                level: selectedNode.level,
                selectedNode: selectedNode,
                metricKeys: selectedContinuousSignals.map(metric => metric.key),
                selectedMetrics: selectedContinuousSignals,
                intervalSeconds: intervalSeconds
            })

            if (requestId !== fetchRequestRef.current) {
                return;
            }

            setCategories(categories)
            setVisibleSeries(seriesData.map(() => true))
            const { coloredSignals, nextAssignment } = allocateColors({
                signals: seriesData,
                prevAssignment: colorMemoryRef.current,
                getKey: s => s.key,
            });
            colorMemoryRef.current = nextAssignment;
            setSeries(coloredSignals);

            setIsContinuousChartLoading(false)
        } catch (err) {
            if (requestId !== fetchRequestRef.current) {
                return;
            }
            setIsContinuousChartLoading(false)
        }
    }

    const resetChartData = async (overrideIntervalSeconds = null) => {
        // Use override interval if provided, otherwise use state value
        const intervalToUse = overrideIntervalSeconds !== null ? overrideIntervalSeconds : intervalSeconds;

        await fetchChartData({
            startDate: currentDates.startDate,
            endDate: currentDates.endDate,
            intervalSeconds: intervalToUse
        });
    }

    const changeSelectedDateFromList = (date) => {
        // Reset zoom state when changing dates
        setIsZoomed(false);
        setZoomedRange(null);
        setIntervalSeconds(300); // Reset to default interval
        setSelectedDate({ key: selectedDate.key + 1, value: date })
    }

    const shouldDisableSecondaryGranularity = (granularity) => {
        if (isRawGranularity) {
            return true
        }

        return false
    }

    const resetChartToLatestData = () => {
        if (!batteryInfo.lastPingedTime) {
            alert("No data available for selected date")
        }

        let lastPingDate = getLocalDateFromUTC(batteryInfo.lastPingedTime)

        let startDate2 = getDatePickerDate(lastPingDate, 0)
        let endDate2 = getDatePickerDate(lastPingDate, 0)

        handleDateChange({ startDate: startDate2, endDate: endDate2 })
    }

    const resetChartDataAfterZoom = (xaxis) => {
        // Clear any existing timeout
        if (zoomTimeoutRef.current) {
            clearTimeout(zoomTimeoutRef.current);
        }

        // Debounce the zoom update to prevent multiple rapid calls
        zoomTimeoutRef.current = setTimeout(async () => {

            // Use the xaxis parameter from beforeZoom event
            const minX = xaxis?.min;
            const maxX = xaxis?.max;

            if (!minX || !maxX) {
                setIsContinuousChartLoading(false);
                return;
            }

            // Calculate the appropriate interval based on the visible time window
            const calculatedInterval = calculateIntervalFromTimeWindow(minX, maxX);

            // Convert minX and maxX timestamps to date strings for the API
            const zoomedStartDate = formatChartTimestampForApi(minX);
            const zoomedEndDate = formatChartTimestampForApi(maxX);

            // Store zoom state
            setIsZoomed(true);
            setZoomedRange({ minX, maxX });
            setIntervalSeconds(calculatedInterval);

            // Fetch new data with the zoomed range
            await fetchChartData({
                startDate: zoomedStartDate,
                endDate: zoomedEndDate,
                intervalSeconds: calculatedInterval
            });
        }, 500); // 500ms debounce
    }

    const handleZoomReset = async () => {
        // Reset interval to default (300 seconds / 5 minutes)
        const defaultInterval = 300;
        setIntervalSeconds(defaultInterval);

        // Reset zoom state
        setIsZoomed(false);
        setZoomedRange(null);

        // Refetch chart data with original date range and default interval
        // Pass defaultInterval directly to avoid using stale state value
        await resetChartData(defaultInterval);
    }

    const toggleSeriesOnLegendClick = (index) => {
        const updatedVisibility = [...visibleSeries];
        updatedVisibility[index] = !updatedVisibility[index];
        setVisibleSeries(updatedVisibility);
        const seriesName = series[index].name;

        if (updatedVisibility[index]) {
            ApexCharts.exec(chartID, 'showSeries', seriesName);
        } else {
            ApexCharts.exec(chartID, 'hideSeries', seriesName);
        }


        if (secondaryGranularity != 'avg') {
            if (updatedVisibility[index]) {
                ApexCharts.exec(chartID, 'showSeries', `${seriesName} mix`);
            } else {
                ApexCharts.exec(chartID, 'hideSeries', `${seriesName} mix`);
            }
        }
    };

    const downloadContinuousSignalsImage = async () => {
        try {
            const chartInstance = ApexCharts.getChartByID(chartID);
            chartInstance.exports.exportToSVG()
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };

    async function exportChartsToOneSVG(chartIDs, chartConfig = {}) {
        const parser = new DOMParser();
        const serializer = new XMLSerializer();

        const combinedSVG = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        combinedSVG.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        combinedSVG.setAttribute("version", "1.1");

        let yOffset = 0;
        let totalWidth = 0;

        const chartSpacing = 30;
        const titleSpacing = 40; // space reserved for title
        const legendSpacing = 20; // space reserved for legends

        for (const id of chartIDs) {
            const cfg = chartConfig[id] || {};
            const chartTitle = cfg.title || `Chart: ${id}`;
            const colorsMap = cfg.colors || {}; // { label: colorCode }

            const chartInstance = ApexCharts.getChartByID(id);
            if (!chartInstance) {
                console.error(`Chart with ID ${id} not found`);
                continue;
            }

            // Temporarily hide built-in legend
            const originalLegend = chartInstance.w.config.legend;
            await chartInstance.updateOptions({ legend: { show: false } }, false, false);

            const svgString = await chartInstance.getSvgString();

            // Restore original legend
            await chartInstance.updateOptions({ legend: originalLegend }, false, false);

            if (!svgString) {
                console.error(`No SVG returned for chart ID ${id}`);
                continue;
            }

            const doc = parser.parseFromString(svgString, "image/svg+xml");
            const svgEl = doc.documentElement;

            const width = parseFloat(svgEl.getAttribute("width")) || 800;
            const height = parseFloat(svgEl.getAttribute("height")) || 300;
            totalWidth = Math.max(totalWidth, width);

            // === TITLE ===
            const titleText = document.createElementNS("http://www.w3.org/2000/svg", "text");
            titleText.textContent = chartTitle;
            titleText.setAttribute("x", 30);
            titleText.setAttribute("y", yOffset + 16);
            // titleText.setAttribute("text-anchor", "middle");
            titleText.setAttribute("font-size", "14");
            titleText.setAttribute("font-weight", "regular");
            titleText.setAttribute("fill", "#000");
            combinedSVG.appendChild(titleText);

            // === CUSTOM LEGENDS ===
            let legendX = 30;
            const legendY = yOffset + titleSpacing; // below title

            Object.entries(colorsMap).forEach(([label, color], index) => {
                const group = document.createElementNS("http://www.w3.org/2000/svg", "g");

                // Circle
                const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
                circle.setAttribute("cx", legendX + 6);
                circle.setAttribute("cy", legendY);
                circle.setAttribute("r", 6);
                circle.setAttribute("fill", color);
                group.appendChild(circle);

                // Text
                const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
                text.textContent = label;
                text.setAttribute("x", legendX + 16);
                text.setAttribute("y", legendY + 4); // adjust baseline
                text.setAttribute("font-size", "12");
                text.setAttribute("fill", "#000");
                group.appendChild(text);

                combinedSVG.appendChild(group);

                // Move next legend item to the right (circle + gap + text width approx)
                legendX += label.length * 5 + 30;
            });

            // === CHART SVG ===
            const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
            g.setAttribute("transform", `translate(30, ${yOffset + titleSpacing + legendSpacing})`);

            while (svgEl.firstChild) {
                g.appendChild(svgEl.firstChild);
            }

            combinedSVG.appendChild(g);

            yOffset += height + chartSpacing + titleSpacing + legendSpacing;
        }

        // === TIME AXIS ===
        const timeAxisHeight = 40;
        const labels = [
            "12 AM", "2 AM", "4 AM", "6 AM", "8 AM", "10 AM",
            "12 PM", "2 PM", "4 PM", "6 PM", "8 PM", "10 PM", "12 AM"
        ];

        const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
        line.setAttribute("x1", 30);
        line.setAttribute("y1", yOffset + 10);
        line.setAttribute("x2", totalWidth + 30);
        line.setAttribute("y2", yOffset + 10);
        line.setAttribute("stroke", "#E9EFF5");
        line.setAttribute("stroke-width", "1");
        combinedSVG.appendChild(line);

        const step = totalWidth / (labels.length - 1);
        labels.forEach((label, i) => {
            const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
            text.textContent = label;
            text.setAttribute("x", i * step + 30);
            text.setAttribute("y", yOffset + 30);
            text.setAttribute("fill", "#7c8b9a");
            text.setAttribute("font-size", "12");
            text.setAttribute("font-weight", "500");
            text.setAttribute("text-anchor", i === 0 ? "start" : (i === labels.length - 1 ? "end" : "middle"));
            combinedSVG.appendChild(text);
        });

        yOffset += timeAxisHeight;

        combinedSVG.setAttribute("width", totalWidth + 30);
        combinedSVG.setAttribute("height", yOffset);

        // === DOWNLOAD SVG ===
        const blob = new Blob([serializer.serializeToString(combinedSVG)], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "categorical-signals.svg";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const downloadBinarySignalsImage = async () => {
        try {
            const finalPreparedSignals = {};
            selectedBinarySignals.forEach(selectedSignal => {
                const possibleValues = Object.values(selectedSignal.possibleValues);
                const statusColorMap = {};
                possibleValues.forEach((status, index) => {
                    statusColorMap[status] = availableBinarySignalColors[index % availableBinarySignalColors.length];
                });
                statusColorMap['noData'] = colorForNoDataBinarySignal;
                finalPreparedSignals[selectedSignal.key] = {
                    title: selectedSignal.label,
                    // segments: formatSegmentsForChart(segments[selectedSignal.key], selectedDate.value),
                    colors: statusColorMap,
                }
            });

            const chartIds = []
            const chartConfig = {}
            selectedBinarySignals.forEach(selectedSignal => {
                const chartId = `binary-signal-chart-${selectedSignal.key}`
                chartIds.push(chartId);
                chartConfig[chartId] = {
                    title: selectedSignal.label,
                    colors: finalPreparedSignals[selectedSignal.key].colors ?? {}
                }
            });
            await exportChartsToOneSVG(chartIds, chartConfig);
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };

    const getCsvName = (signalType = 'continuous') => {

        const signalName = signalType === 'continuous' ? 'Continous Signals' : 'Categorical Signals';

        let csvName = `${selectedNode.displayId} - ${signalName}`;

        csvName = `${csvName} - ${toTitleCase(primaryGranularity)}`;

        if (isRawGranularity) {
            csvName = `${csvName}.csv`;
            return csvName;
        }

        csvName = `${csvName} - ${toTitleCase(secondaryGranularity)}.csv`;

        return csvName;
    }

    const downloadContinuousSignalsData = async () => {
        try {
            let csvData = getCSVDataForExportFromChart({ value: batteryInfo.deviceID }, series, secondaryGranularity, t)
            DownloadCSV(getCsvName('continuous'), csvData);
        } catch (err) {
            console.log(err)
        }
    };

    const downloadBinarySignalsData = async () => {
        try {
            const binarySignalLabels = {}
            allBinarySignals.forEach(signal => {
                binarySignalLabels[signal.key] = signal.label
            });

            const csvData = binarySignalsData.map(data => {
                const formattedMetricData = {};
                data.metricsArray.forEach(metricData => {
                    const label = binarySignalLabels[metricData.type] ?? metricData.type;
                    formattedMetricData[label] = metricData.value ?? '-';
                });

                return {
                    'Date/Time': data.batteryDateTime,
                    ...formattedMetricData,
                }
            });
            DownloadCSV(getCsvName('binary'), csvData);
        } catch (err) {
            console.log(err)
        }
    };

    const setupInitialMetrics = async () => {
        if (!selectedNode.level) {
            return null
        }

        const { binarySignals, continuousSignals } = await getBatterySignalsData(batteryID, selectedNode.level);
        setAllBinarySignals(binarySignals);

        const { formattedContinuousSignals, defaultSelectedContinuousSignals } = prepareContinuousSignalsData(continuousSignals, selectedNode?.level);

        const { coloredSignals, nextAssignment } = allocateColors({
            signals: defaultSelectedContinuousSignals,
            prevAssignment: colorMemoryRef.current,
            getKey: s => s.key,
        });
        colorMemoryRef.current = nextAssignment;

        setSelectedContinuousSignals(coloredSignals);
        setAllContinuousSignals(formattedContinuousSignals);
    };

    const getSecondaryTooltipTitle = (granularity) => {
        if (granularity == 'min-avg-max') {
            return 'Min, Max & Avg. Values'
        }
        return 'Avg. Values'
    }

    const resetBinarySignalsChart = async () => {
        if (!isRawGranularity || !showBinarySignals) {
            setIsBinaryChartLoading(false);
            return
        }
        setIsBinaryChartLoading(true);

        const resampleSeconds = 300;
        const systemID = selectedNode?.id;
        const level = selectedNode.level?.toLowerCase();
        const startTime = selectedDate.value;
        const endTime = selectedDate.value;
        const dates = [startTime, endTime];
        const metricKeys = selectedBinarySignals.map(metric => metric.key);
        const batteryResponseData = await getPackComparisionV4(batteryID, systemID, level, metricKeys, dates, resampleSeconds);
        const rawBatteryResponseData = batteryResponseData?.response?.batteryData ?? [];

        const timezoneConvertedBatteryResponseData = rawBatteryResponseData.map(item => {
            return {
                ...item,
                batteryDateTime: convertAndGetTimeStampInRequiredFormat(item.batteryDateTime),
            }
        });
        setBinarySignalsData(timezoneConvertedBatteryResponseData);
        setIsBinaryChartLoading(false);
    }

    const handleSelectedBinarySignalsUpdate = (updatedSignals) => {
        setSelectedBinarySignals(updatedSignals);
    }

    React.useEffect(() => {
        setIsContinuousChartLoading(true);
        setIsBinaryChartLoading(true);
        setupInitialMetrics();
    }, [selectedNode]);

    React.useEffect(() => {
        setPrimaryGranularity('raw');
        setSecondaryGranularity('avg');
        const selectedDateValue = calculateCarouselData(currentDates.startDate, currentDates.endDate, 'raw')[0]
        changeSelectedDateFromList(selectedDateValue)
    }, [currentDates]);

    React.useEffect(() => {
        const selectedDateValue = calculateCarouselData(currentDates.startDate, currentDates.endDate, primaryGranularity)[0]
        changeSelectedDateFromList(selectedDateValue)
    }, [primaryGranularity]);

    React.useEffect(() => {
        // When primaryGranularity also changed in the same batch (e.g. switching to
        // raw resets secondaryGranularity to avg), the [primaryGranularity] effect
        // already calculates the correct date. Skip here to avoid overwriting it
        // with a stale selectedDate value in the wrong format.
        if (skipSecondaryGranularityEffectRef.current) {
            skipSecondaryGranularityEffectRef.current = false;
            return;
        }

        let selectedDateValue = null;

        if (selectedDate && selectedDate.value) {
            selectedDateValue = selectedDate.value
        }

        if (!selectedDateValue) {
            selectedDateValue = calculateCarouselData(currentDates.startDate, currentDates.endDate, primaryGranularity)[0]
        }

        changeSelectedDateFromList(selectedDateValue)
    }, [secondaryGranularity]);

    React.useEffect(() => {
        if (allBinarySignals.length > 0) {
            setShowBinarySignals(true);
            // As per requirement, in Battery Vitals tab, we need to have
            // "main_status" as the only selected binary signal
            const batteryStatusSignal = allBinarySignals.filter(signal => signal.key === "batteryStatus")
            if (batteryStatusSignal.length) {
                setSelectedBinarySignals(batteryStatusSignal);
            } else {
                setSelectedBinarySignals(allBinarySignals.slice(0, 4));
            }
        }
    }, [allBinarySignals]);

    React.useEffect(() => {
        resetBinarySignalsChart();
    }, [selectedBinarySignals]);

    React.useEffect(() => {
        resetChartData();
    }, [selectedContinuousSignals]);

    React.useEffect(() => {
        resetChartData();
        resetBinarySignalsChart();
    }, [selectedDate]);

    React.useEffect(() => {
        const isChartGranularityEnabled = pageContent?.metaInfo?.globalConfig?.["isChartGranularityEnabled"] !== false;
        if (isChartGranularityEnabled) {
            setShowPrimaryGranularities(true);
            setShowSecondaryGranularities(true);
        }
    }, [batteryID])

    const handleScrollUp = (checkOnly = false) => {
        if (!binarySignalsContainerRef.current || !binarySignalsBlockRef.current) return false;

        const parent = binarySignalsContainerRef.current;
        const block = binarySignalsBlockRef.current;
        const targetY = block.offsetTop - 200;
        const clampedY = Math.max(0, targetY);

        const needsScroll = Math.abs(parent.scrollTop - clampedY) > 5;

        if (checkOnly) {
            return needsScroll;
        }

        if (needsScroll) {
            parent.scrollTo({ top: clampedY, behavior: "smooth" });
        }

        setTimeout(() => {
            const buttonRect = block.getBoundingClientRect();
            if (buttonRect.top < 0) {
                window.scrollTo({ top: 300, behavior: "smooth" });
            }
        }, needsScroll ? 550 : 0);

        return needsScroll;
    };

    if (!selectedNode || !selectedNode.id) {
        return <CartLoader />;
    }

    const metricNames = selectedContinuousSignals.map((metric) => metric.name)

    const nodeIdDisplay = resolveVitalsNodeIdDisplay({
        node: selectedNode,
        showVehicleIdentifier: typeof showBusDeviceInfo === 'function' && showBusDeviceInfo() === true,
        vehicleIdLabel,
        levelLabel: t(`digital_twin:${selectedNode.level}`, toTitleCase(selectedNode.level)),
        licensePlateNo: vehicleInfo?.licensePlateNo,
        deviceID: batteryInfo?.deviceID,
        idDisplayConfig: getVitalsIdDisplayConfig(pageContent, vitalsSection, selectedNode.level),
        batteryInfo,
        vehicleInfo,
    });
    // Pack keeps its ping dot even without an ID; otherwise an empty value hides the box.
    const isPackNode = selectedNode?.level?.toLowerCase() === 'pack';
    const showIdBox = nodeIdDisplay.value !== null || isPackNode;

    const todayDate = moment().tz(timeZone).format("YYYY-MM-DD");

    return (
        <Grid2 sx={{ padding: '16px 0', }}>
            <Grid2 container justifyContent={'space-between'} sx={{ mb: 2 }}>
                <Grid2 container gap={'8px'} px={2.5}>
                    <ChartCalendar
                        startDate={currentDates.startDate}
                        endDate={currentDates.endDate}
                        onDateChange={handleDateChange}
                        dataGapDates={dataGapDates}
                        disableBeforeDate={addedOn}
                        disableAfterDate={todayDate}
                    />

                    {showPrimaryGranularities && <Grid2 container sx={{ background: '#F9F9F9', padding: '4px', borderRadius: '6px' }}>
                        {primaryGranularities.map(granularity => (
                            <PrimaryGranularityButton
                                key={granularity}
                                disabled={shouldDisableGranularity(granularity, currentDates.startDate, currentDates.endDate)}
                                onClick={() => onPrimaryGranularityChange(granularity)}
                                active={primaryGranularity === granularity}>
                                <Typography textTransform="capitalize">{granularity}</Typography>
                            </PrimaryGranularityButton>
                        ))}
                    </Grid2>}
                    {showSecondaryGranularities && <Grid2 container sx={{ background: '#F9F9F9', padding: '4px', borderRadius: '6px' }}>
                        {secondaryGranularities.map(granularity => {
                            const isActive = !isRawGranularity && secondaryGranularity === granularity
                            return (
                                <PrimaryTooltip key={granularity} title={!isRawGranularity ? getSecondaryTooltipTitle(granularity) : undefined} placement="bottom" >
                                    <SecondaryGranularityButton
                                        disabled={shouldDisableSecondaryGranularity(granularity)}
                                        key={granularity}
                                        onClick={() => onSecondaryGranularityChange(granularity)}
                                        active={isActive}>
                                        <CustomIcon height="12px" name={granularity == 'avg' ? "trend-line-icon" : "filled-trend-line-icon"} />
                                    </SecondaryGranularityButton>
                                </PrimaryTooltip>
                            )
                        })}
                    </Grid2>}
                </Grid2>

                <Grid2 container gap={1} alignItems={'center'} px={2.5}>
                    {showIdBox && <IDDisplayBox>

                        {isPackNode &&
                            <PingStatusTooltip online={selectedNode?.info?.pingStatus?.toLowerCase() === 'active'} entityLabel={toTitleCase(t(`digital_twin:${selectedNode.level}`, selectedNode.level))}>
                                <StatusDot sx={{ marginRight: '0px' }} color={selectedNode?.info?.pingStatus?.toLowerCase() === 'active' ? '#09B820' : '#8F8F8F'} />
                            </PingStatusTooltip>}
                        {/* Battery / vehicle / TMS show the plate when the org identifies the asset by it. */}
                        {nodeIdDisplay.value !== null && <Box>
                            <Typography variant="fw400s12c494949">{nodeIdDisplay.label}: </Typography>
                            <Typography variant="fw400s12c494949">
                                {nodeIdDisplay.value}
                            </Typography>
                        </Box>}
                    </IDDisplayBox>}
                    <Box>
                        <ChartActions actions={[
                            { type: 'nothing', label: 'Download Continuous Data', icon: 'download-file-icon', onClick: downloadContinuousSignalsData },
                            ...(showBinarySignals && primaryGranularity === 'raw' ? [{
                                type: 'nothing', label: 'Download Categorical Data', icon: 'download-file-icon', onClick: downloadBinarySignalsData,
                            }] : []),
                            { type: 'nothing', label: 'Download Continuous Image', icon: 'download-image-icon', onClick: downloadContinuousSignalsImage },
                            ...(showBinarySignals && primaryGranularity === 'raw' ? [{
                                type: 'nothing', label: 'Download Categorical Image', icon: 'download-image-icon', onClick: downloadBinarySignalsImage
                            }] : [])
                        ]} />
                    </Box>
                </Grid2>
            </Grid2>

            {/* scrollable fixed height */}
            <Box sx={{ height: showBinarySignals ? '525px' : '475px', overflow: 'auto' }}>
                <Box ref={binarySignalsContainerRef} sx={{ height: '100%', overflowY: 'auto' }}>
                    <Grid2 px={2.5}>
                        <Typography variant="fw500s16c000000" sx={{ lineHeight: '20px' }}>Continuous Signals</Typography>
                        <Grid2 container alignItems={'center'} gap={'12px'} sx={{ mt: 2 }}>
                            <VitalsSelectionDropdown
                                itemList={allContinuousSignals}
                                selectedItems={selectedContinuousSignals}
                                applySelectedItems={applySelectedContinuousSignals}
                                batteryID={batteryID}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {series.filter((s, i) => metricNames.includes(s.name)).map((s, i) => (
                                    <Box
                                        key={i}
                                        onClick={() => toggleSeriesOnLegendClick(i)}
                                        sx={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            opacity: visibleSeries[i] ? 1 : 0.5
                                        }}>
                                        <StatusDot sx={{ marginRight: '8px' }} color={s.color} />
                                        <Typography variant="fw400s12c3D4B5A">{s.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid2>
                        <Box sx={{ mt: 1.5 }}>
                            {
                                secondaryGranularity === 'avg' ?
                                    <VitalsChart
                                        chartID={chartID}
                                        metrics={selectedContinuousSignals}
                                        isLoading={isContinuousChartLoading}
                                        series={series}
                                        categories={categories}
                                        startDate={selectedDate}
                                        primaryGranularity={primaryGranularity}
                                        resetToLatestDataDate={() => resetChartToLatestData()}
                                        visibleSeries={visibleSeries}
                                        onZoom={resetChartDataAfterZoom}
                                        onZoomReset={handleZoomReset}
                                        zoomedRange={zoomedRange}
                                        setLoading={setIsContinuousChartLoading}
                                        isZoomed={isZoomed}
                                    /> : <VitalsChartArea
                                        chartID={chartID}
                                        metrics={selectedContinuousSignals}
                                        isLoading={isContinuousChartLoading}
                                        series={series}
                                        categories={categories}
                                        startDate={selectedDate}
                                        primaryGranularity={primaryGranularity}
                                        resetToLatestDataDate={() => resetChartToLatestData()}
                                        visibleSeries={visibleSeries}
                                        onZoom={resetChartDataAfterZoom}
                                        onZoomReset={handleZoomReset}
                                        zoomedRange={zoomedRange}
                                        setLoading={setIsContinuousChartLoading}
                                        isZoomed={isZoomed}
                                    />
                            }
                        </Box>
                    </Grid2>

                    {showBinarySignals && (
                        <Box ref={binarySignalsBlockRef}>
                            <Divider sx={{ my: 2, borderColor: '#EAEAEA' }} />
                            <Grid2 px={2.5}>
                                <BinarySignals
                                    isLoading={isBinaryChartLoading}
                                    allBinarySignals={allBinarySignals}
                                    binarySignalsData={binarySignalsData}
                                    selectedNode={selectedNode}
                                    currentDates={currentDates}
                                    selectedDate={selectedDate}
                                    primaryGranularity={primaryGranularity}
                                    selectedSignals={selectedBinarySignals}
                                    setSelectedSignals={handleSelectedBinarySignalsUpdate}
                                    handleSelectionOnClick={handleScrollUp}
                                />
                            </Grid2>
                        </Box>
                    )
                    }
                </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: '#EAEAEA' }} />
            <Box sx={{ opacity: isContinuousChartLoading ? 0 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2.5 }}>
                <Box sx={{ width: '70%' }}>
                    <VitalsChartDateCarousel
                        primaryGranularity={primaryGranularity}
                        currentDates={currentDates}
                        onChangeDate={(date) => changeSelectedDateFromList(date)}
                        dataGapDates={dataGapDates}
                    />
                </Box>
            </Box>
        </Grid2 >
    )
}
